import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import OrderManagerTab from '../components/order-manager/OrderManagerTab';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../constants/featureFlags';

const ORDER_TABS = [
  { id: 'to-be-sent', label: 'To Be Sent' },
  { id: 'pending-signature', label: 'Pending Physician Signature' },
  { id: 'received', label: 'Received' },
] as const;

type TabId = (typeof ORDER_TABS)[number]['id'];

const Orders: React.FC = () => {
  const { hasPermission, hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );

  const defaultTabId = ORDER_TABS[0].id;
  const [activeTab, setActiveTab] = useState<TabId>(defaultTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<TabId>>(
    () => new Set([defaultTabId])
  );
  const [refetchKey, setRefetchKey] = useState(0);
  const location = useLocation();
  const isFirstLocationEffect = useRef(true);

  // Refetch order lists when the user navigates back to this page.
  // Skip the initial mount so we don't double-fetch on first load.
  useEffect(() => {
    if (isFirstLocationEffect.current) {
      isFirstLocationEffect.current = false;
      return;
    }
    setRefetchKey(k => k + 1);
  }, [location.pathname]);

  const handleTabChange = useCallback((value: string) => {
    const id = value as TabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  const hasAccess =
    hasPermission('emr:manage') && hasFeatureFlag(FeatureFlag.ORDER_MANAGER);

  if (!hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access the Order Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>Order Manager</h1>
        <p className='text-muted-foreground text-sm'>
          Track orders through checkout, physician signature, and return
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='flex h-auto min-h-8 w-fit gap-0.5 rounded-md border border-border bg-muted/40 p-1'>
          {ORDER_TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className='px-3 py-1.5 text-xs font-medium whitespace-nowrap data-[state=active]:shadow-none sm:text-sm'
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card>
          <CardContent className='py-0'>
            {ORDER_TABS.map(tab => (
              <TabsContent key={tab.id} value={tab.id} className='m-0'>
                {mountedTabIds.has(tab.id) ? (
                  <OrderManagerTab tabId={tab.id} refetchKey={refetchKey} />
                ) : null}
              </TabsContent>
            ))}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default Orders;
